import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Stage, Layer, Line } from 'react-konva';
import FloorPlanGrid from './FloorPlanGrid';
import FloorPlanWall from './FloorPlanWall';
import FloorPlanDimension from './FloorPlanDimension';
import FloorPlanOpening from './FloorPlanOpening';
import FloorPlanFurniture from './FloorPlanFurniture';
import FloorPlanRoom from './FloorPlanRoom';
import { findRooms } from './floorPlanUtils';

const FloorPlanCanvas = React.forwardRef(function FloorPlanCanvas({
  state,
  selectedId,
  drawingWall,
  previewPoint,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onDoubleClick,
  onWheel,
  onSelectElement,
  onWallDragEnd,
  onWallDrag,
  onFurnitureDragEnd,
  onZoomIn,
  onZoomOut,
  onPan,
  activeTool,
}, ref) {
  const containerRef = useRef(null);
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });

  // ResizeObserver for responsive canvas
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) {
        setStageSize({ width, height });
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const { viewport, settings } = state;
  const zoom = viewport.zoom;
  const scale = 1; // 1 pixel = 1 inch at zoom=1

  // Convert stage coordinates to world coordinates
  const stageToWorld = useCallback((stageX, stageY) => {
    return {
      x: (stageX - viewport.x) / zoom,
      y: (stageY - viewport.y) / zoom,
    };
  }, [viewport, zoom]);

  const handleMouseDown = useCallback((e) => {
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    const worldPoint = stageToWorld(pos.x, pos.y);
    onMouseDown(worldPoint, e);
  }, [stageToWorld, onMouseDown]);

  const handleMouseMove = useCallback((e) => {
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    const worldPoint = stageToWorld(pos.x, pos.y);
    onMouseMove(worldPoint, e);
  }, [stageToWorld, onMouseMove]);

  // Wrap zoom handlers to pass actual canvas center
  const handleZoomIn = useCallback(() => {
    onZoomIn(stageSize.width / 2, stageSize.height / 2);
  }, [onZoomIn, stageSize]);

  const handleZoomOut = useCallback(() => {
    onZoomOut(stageSize.width / 2, stageSize.height / 2);
  }, [onZoomOut, stageSize]);

  // Click on empty canvas to deselect
  const handleStageClick = useCallback((e) => {
    // Only deselect if clicking on the stage background, not on an element
    if (e.target === e.target.getStage()) {
      onSelectElement(null);
    }
  }, [onSelectElement]);

  // Detect rooms from walls
  const detectedRooms = React.useMemo(() => {
    return findRooms(state.walls);
  }, [state.walls]);

  return (
    <div
      ref={containerRef}
      className="floor-plan-canvas-container"
      style={{
        flexGrow: 1,
        overflow: 'hidden',
        cursor: drawingWall ? 'crosshair' : 'default',
      }}
    >
      <Stage
        ref={ref}
        width={stageSize.width}
        height={stageSize.height}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={onMouseUp}
        onDblClick={onDoubleClick}
        onDblTap={onDoubleClick}
        onTouchStart={handleMouseDown}
        onTouchMove={handleMouseMove}
        onTouchEnd={onMouseUp}
        onWheel={onWheel}
        onClick={handleStageClick}
        onTap={handleStageClick}
      >
        {/* Grid Layer */}
        <FloorPlanGrid
          width={stageSize.width}
          height={stageSize.height}
          gridSize={settings.gridSize * scale}
          zoom={zoom}
          viewport={viewport}
          unit={settings.unit}
          visible={settings.showGrid}
        />

        {/* Main content layer - transformed by viewport */}
        <Layer
          x={viewport.x}
          y={viewport.y}
          scaleX={zoom}
          scaleY={zoom}
        >
          {/* Detected rooms (rendered behind walls) */}
          {detectedRooms.map((room, i) => (
            <FloorPlanRoom
              key={`detected-${i}`}
              room={{ ...room, id: `detected-${i}`, color: '#E8F0FE' }}
              walls={state.walls}
              unit={settings.unit}
              zoom={zoom}
              isSelected={false}
            />
          ))}

          {/* Saved rooms */}
          {state.rooms.filter(r => r.id !== selectedId).map(room => (
            <FloorPlanRoom
              key={room.id}
              room={room}
              walls={state.walls}
              unit={settings.unit}
              zoom={zoom}
              isSelected={false}
              onSelect={onSelectElement}
            />
          ))}

          {/* Walls */}
          {state.walls.filter(w => w.id !== selectedId).map(wall => (
            <FloorPlanWall
              key={wall.id}
              wall={wall}
              isSelected={false}
              onSelect={onSelectElement}
              onDragEnd={onWallDragEnd}
              onWallDrag={onWallDrag}
              zoom={zoom}
              activeTool={activeTool}
            />
          ))}

          {/* Dimension labels */}
          {settings.showDimensions && state.walls.map(wall => (
            <FloorPlanDimension
              key={`dim-${wall.id}`}
              wall={wall}
              unit={settings.unit}
              zoom={zoom}
            />
          ))}

          {/* Openings */}
          {state.openings.filter(o => o.id !== selectedId).map(opening => (
            <FloorPlanOpening
              key={opening.id}
              opening={opening}
              wall={state.walls.find(w => w.id === opening.wallId)}
              isSelected={false}
              onSelect={onSelectElement}
              zoom={zoom}
              activeTool={activeTool}
            />
          ))}

          {/* Furniture */}
          {state.furniture.filter(f => f.id !== selectedId).map(item => (
            <FloorPlanFurniture
              key={item.id}
              item={item}
              isSelected={false}
              onSelect={onSelectElement}
              onDragEnd={onFurnitureDragEnd}
              zoom={zoom}
              activeTool={activeTool}
            />
          ))}

          {/* Selected item rendered last so its handles are on top */}
          {selectedId && (() => {
            const selectedWall = state.walls.find(w => w.id === selectedId);
            if (selectedWall) return (
              <FloorPlanWall
                key={selectedWall.id}
                wall={selectedWall}
                isSelected={true}
                onSelect={onSelectElement}
                onDragEnd={onWallDragEnd}
                onWallDrag={onWallDrag}
                zoom={zoom}
                activeTool={activeTool}
              />
            );
            const selectedOpening = state.openings.find(o => o.id === selectedId);
            if (selectedOpening) return (
              <FloorPlanOpening
                key={selectedOpening.id}
                opening={selectedOpening}
                wall={state.walls.find(w => w.id === selectedOpening.wallId)}
                isSelected={true}
                onSelect={onSelectElement}
                zoom={zoom}
                activeTool={activeTool}
              />
            );
            const selectedFurniture = state.furniture.find(f => f.id === selectedId);
            if (selectedFurniture) return (
              <FloorPlanFurniture
                key={selectedFurniture.id}
                item={selectedFurniture}
                isSelected={true}
                onSelect={onSelectElement}
                onDragEnd={onFurnitureDragEnd}
                zoom={zoom}
                activeTool={activeTool}
              />
            );
            const selectedRoom = state.rooms.find(r => r.id === selectedId);
            if (selectedRoom) return (
              <FloorPlanRoom
                key={selectedRoom.id}
                room={selectedRoom}
                walls={state.walls}
                unit={settings.unit}
                zoom={zoom}
                isSelected={true}
                onSelect={onSelectElement}
              />
            );
            return null;
          })()}

          {/* Wall drawing preview */}
          {drawingWall && previewPoint && (
            <Line
              points={[drawingWall.x1, drawingWall.y1, previewPoint.x, previewPoint.y]}
              stroke="#C4612E"
              strokeWidth={6 / zoom}
              dash={[8 / zoom, 4 / zoom]}
              opacity={0.7}
              lineCap="round"
              listening={false}
            />
          )}
        </Layer>
      </Stage>

      {/* Pan controls */}
      {onPan && (
        <div className="floor-plan-pan-controls">
          <button
            className="floor-plan-pan-btn floor-plan-pan-up"
            onClick={() => onPan(0, 1)}
            title="Pan up"
            aria-label="Pan up"
          >
            <span className="floor-plan-pan-chevron">&rsaquo;</span>
          </button>
          <div className="floor-plan-pan-middle">
            <button
              className="floor-plan-pan-btn floor-plan-pan-left"
              onClick={() => onPan(1, 0)}
              title="Pan left"
              aria-label="Pan left"
            >
              <span className="floor-plan-pan-chevron">&rsaquo;</span>
            </button>
            <button
              className="floor-plan-pan-btn floor-plan-pan-right"
              onClick={() => onPan(-1, 0)}
              title="Pan right"
              aria-label="Pan right"
            >
              <span className="floor-plan-pan-chevron">&rsaquo;</span>
            </button>
          </div>
          <button
            className="floor-plan-pan-btn floor-plan-pan-down"
            onClick={() => onPan(0, -1)}
            title="Pan down"
            aria-label="Pan down"
          >
            <span className="floor-plan-pan-chevron">&rsaquo;</span>
          </button>
        </div>
      )}

      {/* Zoom controls */}
      <div className="floor-plan-zoom-controls">
        <button
          className="floor-plan-zoom-btn"
          onClick={handleZoomIn}
          title="Zoom in"
          aria-label="Zoom in"
        >
          <i className="bi bi-plus-lg"></i>
        </button>
        <button
          className="floor-plan-zoom-btn"
          onClick={handleZoomOut}
          title="Zoom out"
          aria-label="Zoom out"
        >
          <i className="bi bi-dash-lg"></i>
        </button>
      </div>
    </div>
  );
});

export default React.memo(FloorPlanCanvas);
